import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AdminLogin from './AdminLogin';

function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [editedReview, setEditedReview] = useState(null);
  const [globalMessages, setGlobalMessages] = useState({
    accepted: 'Dziękujemy za dodanie opinii! Twoja opinia została zaakceptowana. W załączniku znajdziesz kod rabatowy o wartości 100 zł, który możesz wykorzystać przy kolejnych zakupach.',
    rejected: 'Dziękujemy za dodanie opinii. Niestety, Twoja opinia wymaga poprawy. Prosimy o ponowne przesłanie zdjęć zgodnie z naszymi wytycznymi.',
    recontact: 'Dziękujemy za dodanie opinii. Skontaktujemy się z Tobą wkrótce w sprawie Twojej opinii.',
    not_yet: ''
  });
  const [rejectionDetails, setRejectionDetails] = useState('');
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [acceptedMessage, setAcceptedMessage] = useState('');
  const [recontactNote, setRecontactNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [newImages, setNewImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState({});
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [pendingAcceptStatus, setPendingAcceptStatus] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedImageModal, setSelectedImageModal] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);
  const [showMultiDeleteConfirm, setShowMultiDeleteConfirm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Sprawdź czy użytkownik jest zalogowany
    const authStatus = localStorage.getItem('adminAuthenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      fetchReviews();
      loadGlobalMessages();
    } else {
      setLoading(false);
    }
  }, []);

  // Obsługa klawiatury dla modala zdjęć
  useEffect(() => {
    if (!selectedImageModal || !selectedReview) return;

    const handleKeyPress = (e) => {
      const allImages = (() => {
        const images = (selectedReview.images || selectedReview.imageUrls || []).filter(url => url && typeof url === 'string' && url.trim() !== '');
        return images.map(url => ({ url: url.trim() }));
      })();

      if (e.key === 'Escape') {
        setSelectedImageModal(null);
        setSelectedImageIndex(0);
      } else if (e.key === 'ArrowLeft' && selectedImageIndex > 0) {
        const newIndex = selectedImageIndex - 1;
        setSelectedImageIndex(newIndex);
        setSelectedImageModal({ url: allImages[newIndex].url, index: newIndex });
      } else if (e.key === 'ArrowRight' && selectedImageIndex < allImages.length - 1) {
        const newIndex = selectedImageIndex + 1;
        setSelectedImageIndex(newIndex);
        setSelectedImageModal({ url: allImages[newIndex].url, index: newIndex });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImageModal, selectedImageIndex, selectedReview]);

  const loadGlobalMessages = () => {
    // Load global messages from localStorage
    const saved = localStorage.getItem('globalClientMessages');
    if (saved) {
      try {
        setGlobalMessages(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading global messages:', e);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuthenticated');
    setIsAuthenticated(false);
    navigate('/');
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/ugc/reviews', {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      setReviews(response.data.reviews || []);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Nieznany błąd';
      setError(`Błąd podczas pobierania opinii: ${errorMessage}`);
      console.error('Error fetching reviews:', err);
      console.error('API URL:', api.defaults.baseURL);
      console.error('Full error:', err.response || err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'not_yet':
        return 'bg-yellow-100 text-yellow-800';
      case 'recontact':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'accepted':
        return 'Zaakceptowane';
      case 'rejected':
        return 'Odrzucone';
      case 'not_yet':
        return 'Do weryfikacji';
      case 'recontact':
        return 'Do ponownego kontaktu';
      default:
        return status;
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Jeśli kliknięto tę samą kolumnę, zmień kierunek
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Jeśli kliknięto nową kolumnę, ustaw ją jako sortowaną i domyślnie rosnąco
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedReviews = () => {
    if (!sortColumn) return reviews;

    const sorted = [...reviews].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'id':
          aValue = a.id || 0;
          bValue = b.id || 0;
          break;
        case 'customerName':
          aValue = (a.customerName || '').toLowerCase();
          bValue = (b.customerName || '').toLowerCase();
          break;
        case 'clientEmail':
          aValue = (a.clientEmail || '').toLowerCase();
          bValue = (b.clientEmail || '').toLowerCase();
          break;
        case 'orderNumber':
          aValue = (a.orderNumber || '').toLowerCase();
          bValue = (b.orderNumber || '').toLowerCase();
          break;
        case 'starRating':
          aValue = a.starRating || 0;
          bValue = b.starRating || 0;
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'discountCode':
          aValue = (a.discountCode || '').toLowerCase();
          bValue = (b.discountCode || '').toLowerCase();
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const handleOpenModal = async (review) => {
    setSelectedReview(review);
    setEditedReview({ ...review });
    // Load existing notes if they exist
    setRejectionDetails(review.rejectionDetails || '');
    // Load rejection message or use default from global config
    setRejectionMessage(review.rejectionMessage || globalMessages.rejected || '');
    // Load accepted message or use default from global config
    setAcceptedMessage(review.acceptedMessage || globalMessages.accepted || '');
    setRecontactNote(review.recontactNote || '');
    // Clear new images when opening modal
    setNewImages([]);
    setImagePreviews({});
    // Clear success and error messages when opening new modal
    setSuccessMessage(null);
    setError(null);

    // Pobierz zdjęcia z R2 na podstawie numeru zamówienia i email
    if (review.orderNumber && review.clientEmail) {
      try {
        const response = await api.get(`/api/ugc/reviews/${review.id}/images`, {
          params: {
            orderNumber: review.orderNumber,
            email: review.clientEmail
          }
        });

        if (response.data.success && response.data.images && response.data.images.length > 0) {
          // Aktualizuj review z pobranymi zdjęciami z R2
          const r2Images = response.data.images.map(img => img.url || img.signedUrl);
          setSelectedReview(prev => ({
            ...prev,
            images: r2Images,
            imageUrls: r2Images,
            imageCount: r2Images.length
          }));
          setEditedReview(prev => ({
            ...prev,
            images: r2Images,
            imageUrls: r2Images,
            imageCount: r2Images.length
          }));
          console.log(`✅ Loaded ${r2Images.length} images from R2 for order ${review.orderNumber}`);
        }
      } catch (err) {
        console.log('⚠️ Could not load images from R2:', err.message);
        // Kontynuuj bez błędów - użyj istniejących danych
      }
    }
  };

  const handleDeleteReview = async () => {
    if (!selectedReview) return;

    try {
      setIsDeleting(true);
      setError(null);

      await api.delete(`/api/ugc/reviews/${selectedReview.id}`, {
        params: {
          orderNumber: selectedReview.orderNumber,
          email: selectedReview.clientEmail
        }
      });

      setSuccessMessage(`Opinia #${selectedReview.id} została usunięta`);
      setShowDeleteConfirm(false);
      
      // Close modal and refresh reviews list
      setTimeout(() => {
      setSelectedReview(null);
      setEditedReview(null);
      setRejectionDetails('');
      setRejectionMessage('');
      setAcceptedMessage('');
      setRecontactNote('');
      fetchReviews();
      }, 1500);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Nieznany błąd';
      setError(`Błąd podczas usuwania opinii: ${errorMessage}`);
      console.error('Error deleting review:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteMultiple = async () => {
    if (selectedReviews.length === 0) return;

    try {
      setIsDeletingMultiple(true);
      setError(null);

      let deletedCount = 0;
      let errorCount = 0;

      for (const reviewId of selectedReviews) {
        try {
          const review = reviews.find(r => r.id === reviewId);
          await api.delete(`/api/ugc/reviews/${reviewId}`, {
            params: {
              orderNumber: review?.orderNumber,
              email: review?.clientEmail
            }
          });
          deletedCount++;
        } catch (err) {
          console.error(`Error deleting review ${reviewId}:`, err);
          errorCount++;
        }
      }

      setShowMultiDeleteConfirm(false);
      setSelectedReviews([]);
      
      if (errorCount === 0) {
        setSuccessMessage(`Usunięto ${deletedCount} opinii`);
      } else {
        setSuccessMessage(`Usunięto ${deletedCount} opinii, ${errorCount} błędów`);
      }
      
      fetchReviews();
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Nieznany błąd';
      setError(`Błąd podczas usuwania opinii: ${errorMessage}`);
      console.error('Error deleting multiple reviews:', err);
    } finally {
      setIsDeletingMultiple(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!editedReview) return;

    // Jeśli zmieniamy status na "accepted", pokaż potwierdzenie
    if (editedReview.status === 'accepted' && selectedReview.status !== 'accepted') {
      setPendingAcceptStatus(editedReview);
      setShowAcceptConfirm(true);
      return;
    }

    await saveReviewChanges();
  };

  const saveReviewChanges = async () => {
    if (!editedReview) return;

    try {
      setIsSaving(true);
      setError(null);

      // Get message for selected status
      let messageForStatus = '';
      
      if (editedReview.status === 'rejected') {
        // For rejected status, use the custom rejection message
        messageForStatus = rejectionMessage.trim() || globalMessages.rejected || '';
      } else if (editedReview.status === 'accepted') {
        // For accepted status, use the custom accepted message
        messageForStatus = acceptedMessage.trim() || globalMessages.accepted || '';
      } else {
        // For other statuses, use global configuration
        messageForStatus = globalMessages[editedReview.status]?.trim() || '';
      }

      // Update review via API
      await api.put(`/api/ugc/reviews/${editedReview.id}`, {
        status: editedReview.status,
        discountCode: editedReview.discountCode || '',
        clientMessage: messageForStatus || null,
        acceptedMessage: editedReview.status === 'accepted' ? acceptedMessage.trim() || null : null,
        rejectionMessage: editedReview.status === 'rejected' ? rejectionMessage.trim() || null : null,
        rejectionDetails: editedReview.status === 'rejected' ? rejectionDetails.trim() || null : null,
        recontactNote: recontactNote.trim() || null // Always save note, regardless of status
      });

      // Update local state with all changes including notes
      setReviews(prevReviews => 
        prevReviews.map(r => {
          if (r.id === editedReview.id) {
            return {
              ...r,
              ...editedReview,
              acceptedMessage: editedReview.status === 'accepted' ? acceptedMessage.trim() : (r.acceptedMessage || null),
              rejectionMessage: editedReview.status === 'rejected' ? rejectionMessage.trim() : (r.rejectionMessage || null),
              rejectionDetails: editedReview.status === 'rejected' ? rejectionDetails.trim() : (r.rejectionDetails || null),
              recontactNote: recontactNote.trim() || null,
              recontactNoteDate: recontactNote.trim() ? new Date().toISOString() : (r.recontactNoteDate || null)
            };
          }
          return r;
        })
      );
      
      // Update selectedReview to show saved data
      setSelectedReview(prev => ({
        ...prev,
        ...editedReview,
        acceptedMessage: editedReview.status === 'accepted' ? acceptedMessage.trim() : (prev?.acceptedMessage || null),
        rejectionMessage: editedReview.status === 'rejected' ? rejectionMessage.trim() : (prev?.rejectionMessage || null),
        rejectionDetails: editedReview.status === 'rejected' ? rejectionDetails.trim() : (prev?.rejectionDetails || null),
        recontactNote: recontactNote.trim() || null,
        recontactNoteDate: recontactNote.trim() ? new Date().toISOString() : (prev?.recontactNoteDate || null)
      }));
      
      // Show success message
      setSuccessMessage('Zmiany zostały zapisane');
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        setSelectedReview(null);
        setEditedReview(null);
        setRejectionDetails('');
        setRejectionMessage('');
        setAcceptedMessage('');
        setRecontactNote('');
        setSuccessMessage(null);
        setShowAcceptConfirm(false);
        setPendingAcceptStatus(null);
      }, 1000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Nieznany błąd';
      setError(`Błąd podczas zapisywania zmian: ${errorMessage}`);
      console.error('Error saving changes:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmAccept = () => {
    if (pendingAcceptStatus) {
      setEditedReview(pendingAcceptStatus);
      setShowAcceptConfirm(false);
      // Zapisz zmiany po potwierdzeniu
      setTimeout(() => {
        saveReviewChanges();
      }, 100);
    }
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={(auth) => {
      setIsAuthenticated(auth);
      if (auth) fetchReviews();
    }} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Ładowanie opinii...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Panel Administracyjny - Opinie</h1>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/admin/images')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Zdjęcia z opinii
              </button>
              <button
                onClick={fetchReviews}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Odśwież
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Wyloguj
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          <div className="mb-4 flex justify-between items-center">
            <div className="text-gray-600">
              Łącznie opinii: <span className="font-bold">{reviews.length}</span>
              {selectedReviews.length > 0 && (
                <span className="ml-4 text-red-600">
                  Zaznaczono: <span className="font-bold">{selectedReviews.length}</span>
                </span>
              )}
            </div>
            {selectedReviews.length > 0 && (
              <button
                onClick={() => setShowMultiDeleteConfirm(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Usuń zaznaczone ({selectedReviews.length})
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200 table-auto text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    
                  </th>
                  <th 
                    className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center">
                      ID
                      <SortIcon column="id" />
                    </div>
                  </th>
                  <th 
                    className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('customerName')}
                  >
                    <div className="flex items-center">
                      KLIENT
                      <SortIcon column="customerName" />
                    </div>
                  </th>
                  <th 
                    className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('clientEmail')}
                  >
                    <div className="flex items-center">
                      EMAIL
                      <SortIcon column="clientEmail" />
                    </div>
                  </th>
                  <th 
                    className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('orderNumber')}
                  >
                    <div className="flex items-center">
                      ZAMÓWIENIE
                      <SortIcon column="orderNumber" />
                    </div>
                  </th>
                  <th 
                    className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('starRating')}
                  >
                    <div className="flex items-center">
                      OCENA
                      <SortIcon column="starRating" />
                    </div>
                  </th>
                  <th 
                    className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      STATUS
                      <SortIcon column="status" />
                    </div>
                  </th>
                  <th 
                    className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('discountCode')}
                  >
                    <div className="flex items-center">
                      KOD
                      <SortIcon column="discountCode" />
                    </div>
                  </th>
                  <th className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    NOTATKI
                  </th>
                  <th 
                    className="px-1 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center">
                      DATA I GODZ.
                      <SortIcon column="createdAt" />
                    </div>
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                    <input
                      type="checkbox"
                      checked={selectedReviews.length === reviews.length && reviews.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedReviews(reviews.map(r => r.id));
                        } else {
                          setSelectedReviews([]);
                        }
                      }}
                      className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                      title="Zaznacz wszystkie"
                    />
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reviews.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-4 text-center text-gray-500">
                      Brak opinii
                    </td>
                  </tr>
                ) : (
                  getSortedReviews().map((review) => (
                    <tr key={review.id} className="hover:bg-gray-50">
                      <td className="px-1 py-2 whitespace-nowrap">
                        <button
                          onClick={() => handleOpenModal(review)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          Zobacz
                        </button>
                      </td>
                      <td className="px-1 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                        #{review.id}
                      </td>
                      <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900 truncate max-w-[100px]" title={review.customerName || '-'}>
                        {review.customerName || '-'}
                      </td>
                      <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-500 truncate max-w-[120px]" title={review.clientEmail || '-'}>
                        {review.clientEmail || '-'}
                      </td>
                      <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900">
                        {review.orderNumber || '-'}
                      </td>
                      <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-900">
                        {review.starRating ? (
                          <span className="flex items-center">
                            <span className="text-yellow-400 mr-1 text-xs">★</span>
                            <span>{review.starRating}/5</span>
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-1 py-2 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 inline-flex items-center text-xs leading-4 font-semibold rounded-full ${getStatusColor(review.status)}`}>
                          {review.status === 'not_yet' && (
                            <svg className="w-2.5 h-2.5 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                          {getStatusLabel(review.status)}
                        </span>
                      </td>
                      <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-500 truncate max-w-[120px]" title={review.discountCode || '-'}>
                        {review.discountCode || '-'}
                      </td>
                      <td className="px-1 py-2 text-xs text-gray-500 max-w-[120px]">
                        {review.recontactNote ? (
                          <div className="truncate" title={review.recontactNote}>
                            <div className="font-medium truncate">{review.recontactNote.substring(0, 30)}{review.recontactNote.length > 30 ? '...' : ''}</div>
                            {review.recontactNoteDate && (
                              <div className="text-xs text-gray-400 mt-0.5">
                                {new Date(review.recontactNoteDate).toLocaleDateString('pl-PL', { 
                                  day: 'numeric', 
                                  month: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-1 py-2 whitespace-nowrap text-xs text-gray-500">
                        {review.createdAt ? new Date(review.createdAt).toLocaleDateString('pl-PL', { 
                          day: 'numeric', 
                          month: 'numeric', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </td>
                      <td className="px-1 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedReviews.includes(review.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedReviews(prev => [...prev, review.id]);
                            } else {
                              setSelectedReviews(prev => prev.filter(id => id !== review.id));
                            }
                          }}
                          className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Modal ze szczegółami opinii */}
          {selectedReview && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Szczegóły opinii #{selectedReview.id}</h2>
                    <button
                      onClick={() => {
                        setSelectedReview(null);
                        setEditedReview(null);
                        setRejectionDetails('');
                        setRejectionMessage('');
                        setAcceptedMessage('');
                        setRecontactNote('');
                        setSuccessMessage(null);
                        setError(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Success message */}
                  {successMessage && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <p className="text-green-800 font-medium">{successMessage}</p>
                      </div>
                    </div>
                  )}

                  {/* Error message */}
                  {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800">{error}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ID</label>
                      <p className="text-gray-900">#{selectedReview.id}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status <span className="text-red-500">*</span></label>
                            <select
                              value={editedReview?.status || selectedReview.status}
                              onChange={(e) => {
                                const newStatus = e.target.value;
                                setEditedReview({ 
                                  ...editedReview, 
                                  status: newStatus,
                                  // Clear discount code if status is not 'accepted'
                                  discountCode: newStatus === 'accepted' ? (editedReview?.discountCode || selectedReview.discountCode || '') : ''
                                });
                                // Set default messages when status changes
                                if (newStatus === 'rejected') {
                                  setRejectionMessage(globalMessages.rejected || '');
                                  setAcceptedMessage('');
                                } else if (newStatus === 'accepted') {
                                  setAcceptedMessage(globalMessages.accepted || '');
                                  setRejectionDetails('');
                                  setRejectionMessage('');
                                } else {
                                  // Clear messages when changing status away from rejected/accepted
                                  setRejectionDetails('');
                                  setRejectionMessage('');
                                  setAcceptedMessage('');
                                }
                                // Keep recontactNote - don't clear it when changing status
                              }}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            >
                        <option value="not_yet">Do weryfikacji</option>
                        <option value="accepted">Zaakceptowane</option>
                        <option value="rejected">Odrzucone</option>
                        <option value="recontact">Do ponownego kontaktu</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Klient</label>
                      <p className="text-gray-900">{selectedReview.customerName || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <p className="text-gray-900">{selectedReview.clientEmail || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Numer zamówienia</label>
                      <p className="text-gray-900">{selectedReview.orderNumber || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ocena</label>
                      <p className="text-gray-900">
                        {selectedReview.starRating ? (
                          <span className="flex items-center">
                            <span className="text-yellow-400 mr-1">★</span>
                            <span>{selectedReview.starRating}/5</span>
                          </span>
                        ) : '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kod rabatowy
                        {editedReview?.status === 'accepted' && <span className="text-gray-500 text-xs ml-1">(tylko dla statusu "Zaakceptowane")</span>}
                      </label>
                      <input
                        type="text"
                        value={editedReview?.discountCode || selectedReview.discountCode || ''}
                        onChange={(e) => setEditedReview({ ...editedReview, discountCode: e.target.value })}
                        placeholder={editedReview?.status === 'accepted' ? "Wpisz kod rabatowy" : "Kod dostępny tylko dla statusu 'Zaakceptowane'"}
                        disabled={editedReview?.status !== 'accepted'}
                        className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                          editedReview?.status === 'accepted' 
                            ? 'border-gray-300 bg-white' 
                            : 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                        }`}
                      />
                      {editedReview?.status !== 'accepted' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Zmień status na "Zaakceptowane", aby móc edytować kod rabatowy
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Data utworzenia</label>
                      <p className="text-gray-900">
                        {selectedReview.createdAt ? new Date(selectedReview.createdAt).toLocaleDateString('pl-PL', { 
                          day: 'numeric', 
                          month: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Opinia</label>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-gray-900 whitespace-pre-wrap">{selectedReview.reviewText || '-'}</p>
                    </div>
                  </div>

                  {/* Wiadomość o akceptacji - pokazywana tylko dla statusu "Zaakceptowane" */}
                  {editedReview && editedReview.status === 'accepted' && (
                    <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Wiadomość o akceptacji <span className="text-green-500">*</span>
                      </label>
                      <textarea
                        value={acceptedMessage}
                        onChange={(e) => setAcceptedMessage(e.target.value)}
                        placeholder="Wpisz wiadomość, która zostanie wysłana do klienta..."
                        rows="6"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Ta wiadomość zostanie wysłana do klienta wraz z kodem rabatowym. Możesz ją edytować pod konkretną akceptację.
                      </p>
                    </div>
                  )}

                  {/* Wiadomość o odrzuceniu - pokazywana tylko dla statusu "Odrzucone" */}
                  {editedReview && editedReview.status === 'rejected' && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Wiadomość o odrzuceniu <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={rejectionMessage}
                        onChange={(e) => setRejectionMessage(e.target.value)}
                        placeholder="Wpisz wiadomość, która zostanie wysłana do klienta..."
                        rows="6"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Ta wiadomość zostanie wysłana do klienta. Możesz ją edytować pod konkretne odrzucenie.
                      </p>
                    </div>
                  )}

                  {/* Zdjęcia */}
                  <div className="mt-6">
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Zdjęcia ({selectedReview.images && selectedReview.images.length > 0 ? selectedReview.images.length : selectedReview.imageCount || 0})
                      </label>
                      {editedReview && (
                        <label className="cursor-pointer px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors">
                          <input
                            type="file"
                            multiple
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              if (files.length > 0) {
                                const validFiles = files.filter(file => {
                                  const maxSize = 5 * 1024 * 1024; // 5MB
                                  return file.size <= maxSize && 
                                         ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
                                });
                                
                                if (validFiles.length !== files.length) {
                                  setError('Niektóre pliki są zbyt duże lub mają nieprawidłowy format. Maksymalny rozmiar: 5MB, formaty: JPG, PNG, WEBP');
                                }
                                
                                setNewImages(prev => [...prev, ...validFiles]);
                                
                                // Create previews
                                validFiles.forEach(file => {
                                  const reader = new FileReader();
                                  reader.onload = (e) => {
                                    setImagePreviews(prev => ({ ...prev, [file.name]: e.target.result }));
                                  };
                                  reader.readAsDataURL(file);
                                });
                              }
                              e.target.value = ''; // Reset input
                            }}
                            disabled={isUploadingImages}
                          />
                          + Dodaj zdjęcia
                        </label>
                      )}
                    </div>
                    {(() => {
                      // Zdjęcia są TYLKO w R2 - używamy tylko imageUrls pobranych z R2
                      let images = [];
                      
                      // TYLKO imageUrls z R2 (pobrane przez handleOpenModal)
                      if (selectedReview.imageUrls && Array.isArray(selectedReview.imageUrls) && selectedReview.imageUrls.length > 0) {
                        images = selectedReview.imageUrls.filter(img => img && typeof img === 'string' && img.trim() !== '');
                      }
                      
                      // Debug logging
                      console.log('Review images from R2:', {
                        id: selectedReview.id,
                        orderNumber: selectedReview.orderNumber,
                        clientEmail: selectedReview.clientEmail,
                        imageUrls: selectedReview.imageUrls,
                        imageCount: images.length,
                        finalImages: images
                      });
                      
                      const allImages = images;
                      
                      if (allImages.length > 0) {
                        return (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {allImages.map((imageUrl, index) => {
                              // Ensure URL is valid
                              const validUrl = imageUrl && typeof imageUrl === 'string' && imageUrl.trim() !== '' 
                                ? imageUrl.trim() 
                                : null;
                              
                              if (!validUrl) return null;
                              
                              return (
                                <div 
                                  key={index} 
                                  className="relative group"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('Opening image modal:', { url: validUrl, index });
                                    setSelectedImageModal({ url: validUrl, index });
                                    setSelectedImageIndex(index);
                                  }}
                                >
                                  <img
                                    src={validUrl}
                                    alt={`Zdjęcie ${index + 1}`}
                                    className="w-full h-48 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      console.log('Opening image modal from img:', { url: validUrl, index });
                                      setSelectedImageModal({ url: validUrl, index });
                                      setSelectedImageIndex(index);
                                    }}
                                    onError={(e) => {
                                      console.error(`Failed to load image ${index + 1}:`, validUrl);
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `
                                        <div class="w-full h-48 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                                          <span class="text-gray-400 text-sm">Błąd ładowania</span>
                                        </div>
                                      `;
                                    }}
                                    onLoad={() => {
                                      console.log(`Successfully loaded image ${index + 1}:`, validUrl);
                                    }}
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg flex items-center justify-center">
                                    <span className="text-white opacity-0 group-hover:opacity-100 text-sm">Kliknij aby powiększyć</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      } else {
                        return (
                          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-gray-700 font-medium">Brak zdjęć dla tej opinii w R2</p>
                            <p className="text-sm text-gray-500 mt-1">
                              Numer zamówienia: {selectedReview.orderNumber || 'brak'} | 
                              Email: {selectedReview.clientEmail || 'brak'} | 
                              Sprawdź folder: {selectedReview.id}_{selectedReview.orderNumber}/
                            </p>
                          </div>
                        );
                      }
                    })()}

                    {/* Nowe zdjęcia do dodania */}
                    {newImages.length > 0 && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nowe zdjęcia do dodania ({newImages.length})
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {newImages.map((file, index) => (
                            <div key={index} className="relative group">
                              {imagePreviews[file.name] ? (
                                <img
                                  src={imagePreviews[file.name]}
                                  alt={`Nowe zdjęcie ${index + 1}`}
                                  className="w-full h-48 object-cover rounded-lg border border-blue-300"
                                />
                              ) : (
                                <div className="w-full h-48 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                                  <span className="text-gray-400">Ładowanie...</span>
                                </div>
                              )}
                              <button
                                onClick={() => {
                                  setNewImages(prev => prev.filter((_, i) => i !== index));
                                  setImagePreviews(prev => {
                                    const newPreviews = { ...prev };
                                    delete newPreviews[file.name];
                                    return newPreviews;
                                  });
                                }}
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                              <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                                {file.name}
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={async () => {
                            if (newImages.length === 0) return;
                            
                            try {
                              setIsUploadingImages(true);
                              setError(null);
                              
                              const formData = new FormData();
                              newImages.forEach(file => {
                                formData.append('images', file);
                              });
                              formData.append('orderNumber', selectedReview.orderNumber || 'unknown');
                              
                              console.log('📸 Uploading images to review:', selectedReview.id);
                              console.log('📸 Order number:', selectedReview.orderNumber);
                              console.log('📸 Files:', newImages.map(f => ({ name: f.name, size: f.size, type: f.type })));
                              
                              const response = await api.post(`/api/ugc/reviews/${selectedReview.id}/images`, formData, {
                                headers: {
                                  'Content-Type': undefined,
                                },
                                maxContentLength: Infinity,
                                maxBodyLength: Infinity,
                              });
                              
                              console.log('📸 Upload response:', response.data);
                              
                              // Refresh review data
                              await fetchReviews();
                              
                              // Update selected review
                              const updatedReview = reviews.find(r => r.id === selectedReview.id);
                              if (updatedReview) {
                                setSelectedReview(updatedReview);
                                setEditedReview({ ...editedReview, ...updatedReview });
                              }
                              
                              // Clear new images
                              setNewImages([]);
                              setImagePreviews({});
                              
                              setSuccessMessage(`Dodano ${newImages.length} zdjęć`);
                            } catch (err) {
                              const errorMessage = err.response?.data?.message || err.message || 'Nieznany błąd';
                              setError(`Błąd podczas dodawania zdjęć: ${errorMessage}`);
                              console.error('Error uploading images:', err);
                            } finally {
                              setIsUploadingImages(false);
                            }
                          }}
                          disabled={isUploadingImages || newImages.length === 0}
                          className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {isUploadingImages ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Dodawanie zdjęć...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              <span>Zapisz zdjęcia ({newImages.length})</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Notatka dla customer care - pokazywana zawsze */}
                  {editedReview && (
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Notatka dla customer care
                        </label>
                        {(selectedReview.recontactNoteDate || recontactNote.trim()) && (
                          <span className="text-xs text-gray-500">
                            {selectedReview.recontactNoteDate 
                              ? `Data dodania: ${new Date(selectedReview.recontactNoteDate).toLocaleDateString('pl-PL', { 
                                  day: 'numeric', 
                                  month: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}`
                              : 'Data zostanie zapisana po zapisaniu notatki'}
                          </span>
                        )}
                      </div>
                      <textarea
                        value={recontactNote}
                        onChange={(e) => setRecontactNote(e.target.value)}
                        placeholder="Dodaj notatkę dla działu customer care, która pomoże w kontakcie z klientem (np. powód kontaktu, co należy omówić, itp.)..."
                        rows="4"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Ta notatka będzie widoczna dla działu customer care przy kontakcie z klientem.
                      </p>
                    </div>
                  )}

                  <div className="mt-6 flex justify-between items-center">
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Usuń opinię</span>
                    </button>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setSelectedReview(null);
                          setEditedReview(null);
                          setRejectionDetails('');
                          setRejectionMessage('');
                          setAcceptedMessage('');
                          setRecontactNote('');
                        }}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        Zamknij
                      </button>
                      {editedReview && (
                        <button
                          onClick={handleSaveChanges}
                          disabled={isSaving || (editedReview.status === 'rejected' && !rejectionMessage.trim()) || (editedReview.status === 'accepted' && !acceptedMessage.trim())}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {isSaving ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Zapisywanie...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>Zapisz zmiany</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modal potwierdzenia usunięcia */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Usuń opinię</h3>
                  </div>
                </div>
                
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Czy na pewno chcesz usunąć opinię #{selectedReview?.id}?
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-800 mb-2">⚠️ Uwaga:</p>
                    <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                      <li>Ta operacja jest nieodwracalna</li>
                      <li>Zostaną usunięte wszystkie dane opinii</li>
                      <li>Zostaną usunięte wszystkie zdjęcia z R2</li>
                      <li>Zostaną usunięte wszystkie rekordy z bazy danych</li>
                    </ul>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={handleDeleteReview}
                    disabled={isDeleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isDeleting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Usuwanie...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span>Usuń</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal potwierdzenia masowego usunięcia */}
          {showMultiDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Usuń zaznaczone opinie</h3>
                  </div>
                </div>
                
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Czy na pewno chcesz usunąć <strong>{selectedReviews.length}</strong> zaznaczonych opinii?
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-800 mb-2">⚠️ Uwaga:</p>
                    <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                      <li>Ta operacja jest nieodwracalna</li>
                      <li>Zostaną usunięte wszystkie dane opinii</li>
                      <li>Zostaną usunięte wszystkie zdjęcia z R2</li>
                      <li>Zostaną usunięte wszystkie rekordy z bazy danych</li>
                    </ul>
                  </div>
                  <div className="mt-4 max-h-32 overflow-y-auto bg-gray-50 rounded-lg p-2">
                    <p className="text-xs text-gray-500 mb-1">ID opinii do usunięcia:</p>
                    <p className="text-sm text-gray-700">
                      {selectedReviews.map(id => `#${id}`).join(', ')}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowMultiDeleteConfirm(false)}
                    disabled={isDeletingMultiple}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={handleDeleteMultiple}
                    disabled={isDeletingMultiple}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isDeletingMultiple ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Usuwanie...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span>Usuń wszystkie ({selectedReviews.length})</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal potwierdzenia akceptacji */}
          {showAcceptConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Potwierdź akceptację opinii</h3>
                  </div>
                </div>
                
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Czy na pewno chcesz zaakceptować opinię #{selectedReview.id}?
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-yellow-800 mb-2">⚠️ Uwaga:</p>
                    <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                      <li>Status opinii zostanie zmieniony na "Zaakceptowane"</li>
                      <li>Klient otrzyma email z kodem rabatowym</li>
                      <li>Upewnij się, że kod rabatowy jest poprawny</li>
                    </ul>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowAcceptConfirm(false);
                      setPendingAcceptStatus(null);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={handleConfirmAccept}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Tak, akceptuję opinię
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Modal do powiększania zdjęć */}
      {selectedImageModal && selectedReview && (() => {
        const allImages = (() => {
          const images = (selectedReview.images || selectedReview.imageUrls || []).filter(url => url && typeof url === 'string' && url.trim() !== '');
          return images.map(url => ({ url: url.trim() }));
        })();
        
        console.log('Rendering image modal:', { selectedImageModal, allImages, selectedImageIndex });
        
        return (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center p-4"
            onClick={() => {
              setSelectedImageModal(null);
              setSelectedImageIndex(0);
            }}
          >
            <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
              {/* Przycisk zamknij */}
              <button
                onClick={() => {
                  setSelectedImageModal(null);
                  setSelectedImageIndex(0);
                }}
                className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2"
                aria-label="Zamknij"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Przycisk poprzednie zdjęcie */}
              {allImages.length > 1 && selectedImageIndex > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = selectedImageIndex - 1;
                    setSelectedImageIndex(newIndex);
                    setSelectedImageModal({ url: allImages[newIndex].url, index: newIndex });
                  }}
                  className="absolute left-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-3"
                  aria-label="Poprzednie zdjęcie"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}

              {/* Przycisk następne zdjęcie */}
              {allImages.length > 1 && selectedImageIndex < allImages.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = selectedImageIndex + 1;
                    setSelectedImageIndex(newIndex);
                    setSelectedImageModal({ url: allImages[newIndex].url, index: newIndex });
                  }}
                  className="absolute right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-3"
                  aria-label="Następne zdjęcie"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {/* Zdjęcie */}
              <img
                src={selectedImageModal.url}
                alt={`Zdjęcie ${selectedImageIndex + 1}`}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
                onError={(e) => {
                  console.error('Failed to load image in modal:', selectedImageModal.url);
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ccc" width="400" height="300"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="20" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EBłąd ładowania%3C/text%3E%3C/svg%3E';
                }}
              />

              {/* Informacja o numerze zdjęcia */}
              {allImages.length > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg text-sm">
                  {selectedImageIndex + 1} / {allImages.length}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default AdminPanel;

